import React, { useState, useEffect, useCallback } from 'react';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { GPTService } from '../../../services/gptService';
import { toast } from 'react-hot-toast';
import PrimeStateForm from '../PrimeStateForm';
import PrimeTransitionForm from '../PrimeTransitionForm';
import { PrimeProcessForm } from '../PrimeProcessForm';
import {
  ProcessType,
  DemandType,
  ArtifactStateTransitionType,
  DemandFlowType,
} from '../../../types/enums';
import './GPTGenerationTab.css';

interface GPTResponse {
  id: string;
  prompt: string;
  raw_response: string;
  response_type: string;
  is_approved: boolean;
  is_processed: boolean;
  created_at: string;
}

interface GPTResponseComponent {
  type: 'state' | 'transition' | 'process';
  data: ComponentData;
}

interface ComponentData {
  name: string;
  state_type?: ArtifactStateTransitionType;
  demand_type?: DemandType;
  demand_flow_type?: DemandFlowType;
  is_initial?: boolean;
  is_final?: boolean;
  from_state?: string;
  to_state?: string;
  process_name?: string;
  process_type?: ProcessType;
  prime_artifact_ref?: string;
  prime_process_ref?: string;
  from_state_name?: string;
  to_state_name?: string;
  process_parameters?: Record<string, unknown>;
  demand_calculation_rules?: Record<string, unknown>;
  validation_rules?: Record<string, unknown>;
  improvement_ref?: string;
  improvement_parameters?: Record<string, unknown>;
}

interface GPTGenerationTabProps {
  primeartifact: PrimeArtifact;
}

const GPTGenerationTab: React.FC<GPTGenerationTabProps> = ({ primeartifact }) => {
  console.log('[GPTGenerationTab] Component rendering with artifact:', {
    id: primeartifact.id,
    name: primeartifact.name,
  });

  const [isGeneratingModel, setIsGeneratingModel] = useState(false);
  const [gptResponses, setGptResponses] = useState<GPTResponse[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<GPTResponse | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<GPTResponseComponent | null>(null);
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [isPromptModified, setIsPromptModified] = useState(false);
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  const fetchExistingResponses = useCallback(async () => {
    try {
      console.log('[GPTGenerationTab] Fetching responses for artifact:', primeartifact.id);
      const responses = await GPTService.getResponses(primeartifact.id);
      console.log('[GPTGenerationTab] Received responses:', responses);
      setGptResponses(responses);
      if (responses.length > 0) {
        setSelectedResponse(responses[0]);
      }
    } catch (error) {
      console.error('[GPTGenerationTab] Error fetching responses:', error);
      toast.error('Failed to fetch existing responses');
    }
  }, [primeartifact.id]);

  // Fetch existing responses when the component mounts
  useEffect(() => {
    console.log('[GPTGenerationTab] Component mounted, fetching responses');
    if (primeartifact) {
      console.log('[GPTGenerationTab] Current artifact data:', {
        id: primeartifact.id,
        name: primeartifact.name,
        states: primeartifact.states?.length || 0,
        transitions: primeartifact.transitions?.length || 0,
        processes: primeartifact.processes?.length || 0,
      });
      void fetchExistingResponses();
    }
  }, [primeartifact, fetchExistingResponses]);

  // Add effect to log when artifact data changes
  useEffect(() => {
    console.log('[GPTGenerationTab] Artifact data updated:', {
      id: primeartifact.id,
      name: primeartifact.name,
      states: primeartifact.states?.length || 0,
      transitions: primeartifact.transitions?.length || 0,
      processes: primeartifact.processes?.length || 0,
    });
  }, [primeartifact.states, primeartifact.transitions, primeartifact.processes]);

  // Initialize prompt when artifact changes
  useEffect(() => {
    if (primeartifact) {
      const defaultPrompt = generateDefaultPrompt(primeartifact);
      setCurrentPrompt(defaultPrompt);
    }
  }, [primeartifact]);

  const generateDefaultPrompt = (artifact: PrimeArtifact): string => {
    return `Generate a complete model for ${artifact.name} (${artifact.artifact_type_name}) with the following details:
- Name: ${artifact.name}
- Type: ${artifact.artifact_type_name}
- Description: ${artifact.description || 'No description provided'}

Please generate:
1. A set of states that represent the lifecycle of this artifact
2. Transitions between these states
3. Processes that handle the transitions

For each component, include:
- Name and description
- Type and parameters
- Validation rules
- Improvement references if applicable`;
  };

  const handlePromptEdit = (newPrompt: string) => {
    setCurrentPrompt(newPrompt);
    setIsPromptModified(true);
  };

  const handleCreateNewPrompt = () => {
    if (selectedResponse) {
      setCurrentPrompt(selectedResponse.prompt);
      setIsPromptModified(false);
      setShowPromptEditor(true);
    }
  };

  const handleGenerateModel = async () => {
    if (!primeartifact) return;

    // If there's an existing response and the prompt hasn't been modified, show a message
    if (gptResponses.length > 0 && !isPromptModified) {
      toast.error('Please modify the prompt before generating a new response');
      return;
    }

    try {
      setIsGeneratingModel(true);
      const response = await GPTService.generateCompleteModel(currentPrompt);

      if (response && (response as { responses?: unknown[] }).responses) {
        const responseData = response as { responses: unknown[] };
        setGptResponses(responseData.responses as GPTResponse[]);
        if (responseData.responses.length > 0) {
          setSelectedResponse(responseData.responses[0] as GPTResponse);
        }
        setIsPromptModified(false);
        setShowPromptEditor(false);
      }
    } catch (error) {
      console.error('Error generating model:', error);
      toast.error('Failed to generate model');
    } finally {
      setIsGeneratingModel(false);
    }
  };

  const renderPromptEditor = () => (
    <div className="prompt-editor">
      <h3>Edit Generation Prompt</h3>
      <textarea
        value={currentPrompt}
        onChange={(e) => handlePromptEdit(e.target.value)}
        className="prompt-textarea"
        rows={10}
      />
      <div className="prompt-actions">
        <button
          className="generate-model-button"
          onClick={handleGenerateModel}
          disabled={isGeneratingModel || !isPromptModified}
        >
          {isGeneratingModel ? 'Generating...' : 'Generate Component Model'}
        </button>
        <button
          className="cancel-button"
          onClick={() => {
            setShowPromptEditor(false);
            setIsPromptModified(false);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderResponseComparison = () => {
    if (!selectedResponse) return null;

    console.log('[GPTGenerationTab] PrimeArtifact data:', {
      id: primeartifact.id,
      name: primeartifact.name,
      states: primeartifact.states,
      transitions: primeartifact.transitions,
      processes: primeartifact.processes,
    });

    let modelData;
    try {
      modelData =
        typeof selectedResponse.raw_response === 'string'
          ? JSON.parse(selectedResponse.raw_response)
          : selectedResponse.raw_response;
      console.log('[GPTGenerationTab] Parsed model data:', modelData);
    } catch (error) {
      console.error('[GPTGenerationTab] Error parsing response:', error);
      return <div className="error-message">Invalid response format</div>;
    }

    const existingStates = primeartifact.states || [];
    const existingTransitions = primeartifact.transitions || [];
    const existingProcesses = primeartifact.processes || [];

    console.log('[GPTGenerationTab] Existing components:', {
      states: existingStates,
      transitions: existingTransitions,
      processes: existingProcesses,
    });

    return (
      <div className="response-comparison">
        <div className="comparison-section">
          <h4>States</h4>
          <div className="comparison-grid">
            <div className="recommended-items">
              <h5>Recommended States</h5>
              <div className="component-list">
                {modelData.states?.map((state: unknown, index: number) => (
                  <div
                    key={index}
                    className="component-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectComponent(selectedResponse, 'state', index);
                    }}
                  >
                    <span className="component-name">{(state as { name: string }).name}</span>
                    <span className="component-description">
                      {(state as { description: string }).description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="existing-items">
              <h5>Existing States</h5>
              <div className="component-list">
                {existingStates.map((state) => (
                  <div key={state.id} className="component-item">
                    <span className="component-name">{state.name}</span>
                    <span className="component-description">{state.description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="comparison-section">
          <h4>Transitions</h4>
          <div className="comparison-grid">
            <div className="recommended-items">
              <h5>Recommended Transitions</h5>
              <div className="component-list">
                {modelData.transitions?.map((transition: unknown, index: number) => (
                  <div
                    key={index}
                    className="component-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectComponent(selectedResponse, 'transition', index);
                    }}
                  >
                    <span className="component-name">{(transition as { name: string }).name}</span>
                    <span className="component-info">
                      {(transition as { from_state: string }).from_state} →{' '}
                      {(transition as { to_state: string }).to_state}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="existing-items">
              <h5>Existing Transitions</h5>
              <div className="component-list">
                {existingTransitions.map((transition) => {
                  const fromState = existingStates.find((s) => s.id === transition.from_state_ref);
                  const toState = existingStates.find((s) => s.id === transition.to_state_ref);
                  return (
                    <div key={transition.id} className="component-item">
                      <span className="component-name">{transition.name}</span>
                      <span className="component-info">
                        {fromState?.name} → {toState?.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="comparison-section">
          <h4>Processes</h4>
          <div className="comparison-grid">
            <div className="recommended-items">
              <h5>Recommended Processes</h5>
              <div className="component-list">
                {modelData.processes?.map((process: unknown, index: number) => (
                  <div
                    key={index}
                    className="component-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectComponent(selectedResponse, 'process', index);
                    }}
                  >
                    <span className="component-name">{(process as { name: string }).name}</span>
                    <span className="component-type">{(process as { type: string }).type}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="existing-items">
              <h5>Existing Processes</h5>
              <div className="component-list">
                {existingProcesses.map((process) => (
                  <div key={process.id} className="component-item">
                    <span className="component-name">{process.name}</span>
                    <span className="component-type">{process.process_type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleApproveResponse = async () => {
    if (!selectedResponse) return;

    try {
      await GPTService.processResponse(selectedResponse.id);
      setGptResponses((prev) =>
        prev.map((response) =>
          response.id === selectedResponse.id
            ? { ...response, is_approved: true, is_processed: true }
            : response
        )
      );

      toast.success('Response processed successfully');
    } catch (error: unknown) {
      console.error('Error processing response:', error);
      let errorMessage = 'Failed to process response';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleDeleteResponse = async (responseId: string) => {
    try {
      setGptResponses((prev) => prev.filter((response) => response.id !== responseId));
      if (selectedResponse?.id === responseId) {
        setSelectedResponse(null);
      }
      toast.success('Response deleted successfully');
    } catch (error) {
      console.error('Error deleting response:', error);
      toast.error('Failed to delete response');
    }
  };

  const handleSelectComponent = (
    response: GPTResponse,
    componentType: 'state' | 'transition' | 'process',
    index: number
  ) => {
    try {
      const modelData =
        typeof response.raw_response === 'string'
          ? JSON.parse(response.raw_response)
          : response.raw_response;

      let componentData: ComponentData | undefined;
      switch (componentType) {
        case 'state':
          componentData = modelData.states?.[index];
          if (componentData) {
            componentData = {
              ...componentData,
              state_type: (componentData.state_type?.toUpperCase() ||
                'PROCESSITEM') as ArtifactStateTransitionType,
              demand_type: (componentData.demand_type?.toUpperCase() || 'TARGET') as DemandType,
              demand_flow_type: (componentData.demand_flow_type?.toUpperCase() ||
                'DRUM') as DemandFlowType,
            };
          }
          break;
        case 'transition':
          componentData = modelData.transitions?.[index];
          if (componentData && primeartifact) {
            const allStates = modelData.states || [];
            const allProcesses = modelData.processes || [];
            const inputState = allStates.find(
              (s: unknown) => (s as { name: string }).name === componentData?.from_state
            );
            const outputState = allStates.find(
              (s: unknown) => (s as { name: string }).name === componentData?.to_state
            );
            const process = allProcesses.find(
              (p: unknown) => (p as { name: string }).name === componentData?.process_name
            );

            componentData = {
              ...componentData,
              from_state: inputState?.id || null,
              to_state: outputState?.id || null,
              prime_process_ref: process?.id || null,
              prime_artifact_ref: primeartifact.id,
              from_state_name: componentData.from_state,
              to_state_name: componentData.to_state,
              process_name: componentData.process_name,
            };
          }
          break;
        case 'process':
          componentData = modelData.processes?.[index];
          if (componentData && primeartifact) {
            componentData = {
              ...componentData,
              process_type: (componentData.process_type?.toUpperCase() || 'DEMAND') as ProcessType,
              demand_flow_type: (componentData.demand_flow_type?.toUpperCase() ||
                'DRUM') as DemandFlowType,
              prime_artifact_ref: primeartifact.id,
              process_parameters: componentData.process_parameters || {},
              demand_calculation_rules: componentData.demand_calculation_rules || {},
              validation_rules: componentData.validation_rules || {},
              improvement_ref: componentData.improvement_ref || undefined,
              improvement_parameters: componentData.improvement_parameters || {},
            };
          }
          break;
      }

      if (componentData) {
        setSelectedComponent({
          type: componentType,
          data: componentData,
        });
        setShowComponentForm(true);
      }
    } catch (error) {
      console.error('Error parsing component data:', error);
      toast.error('Failed to parse component data');
    }
  };

  const handleComponentFormSubmit = async (data: Record<string, unknown>) => {
    if (!selectedComponent || !primeartifact) return;

    try {
      const prompt = typeof data === 'string' ? data : JSON.stringify(data);

      switch (selectedComponent.type) {
        case 'state':
          await GPTService.generateStates(primeartifact.id, prompt);
          break;
        case 'transition':
          await GPTService.generateTransitions(primeartifact.id, prompt);
          break;
        case 'process':
          await GPTService.generateProcesses(primeartifact.id, prompt);
          break;
      }

      toast.success(`${selectedComponent.type} created successfully`);
      setShowComponentForm(false);
      setSelectedComponent(null);
    } catch (error) {
      console.error(`Error creating ${selectedComponent.type}:`, error);
      toast.error(`Failed to create ${selectedComponent.type}`);
    }
  };

  const handleComponentFormCancel = () => {
    setShowComponentForm(false);
    setSelectedComponent(null);
  };

  return (
    <div className="gpt-generation-tab">
      {showPromptEditor ? (
        renderPromptEditor()
      ) : (
        <>
          {gptResponses.length > 0 ? (
            <div className="existing-responses">
              <div className="response-header">
                <h3>Generated Models</h3>
                <button className="new-prompt-button" onClick={handleCreateNewPrompt}>
                  Create New Prompt
                </button>
              </div>
              <div className="response-list">
                {gptResponses.map((response) => (
                  <div
                    key={response.id}
                    className={`response-item ${selectedResponse?.id === response.id ? 'selected' : ''}`}
                    onClick={() => setSelectedResponse(response)}
                  >
                    <div className="response-header">
                      <span className="response-date">
                        {new Date(response.created_at).toLocaleString()}
                      </span>
                      <div className="response-actions">
                        {!response.is_approved && (
                          <button
                            className="approve-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleApproveResponse();
                            }}
                          >
                            Approve
                          </button>
                        )}
                        <button
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeleteResponse(response.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {selectedResponse?.id === response.id && (
                      <div className="response-details">
                        <div className="prompt-display">
                          <h4>Generation Prompt</h4>
                          <pre>{response.prompt}</pre>
                        </div>
                        {renderResponseComparison()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="no-responses">
              <h3>No Generated Models</h3>
              <p>Click the button below to generate a model for this artifact.</p>
              <button className="generate-model-button" onClick={() => setShowPromptEditor(true)}>
                Generate Component Model
              </button>
            </div>
          )}
        </>
      )}

      {showComponentForm && selectedComponent && (
        <div className="component-form-modal">
          <div className="component-form-content">
            <h3>Create {selectedComponent.type}</h3>
            {selectedComponent.type === 'state' && (
              <PrimeStateForm
                initialData={{
                  ...selectedComponent.data,
                  prime_artifact_ref: primeartifact.id,
                }}
                onSubmit={(state: unknown) =>
                  handleComponentFormSubmit(state as Record<string, unknown>)
                }
                onBack={handleComponentFormCancel}
              />
            )}
            {selectedComponent.type === 'transition' && (
              <PrimeTransitionForm
                prime_artifact_id={primeartifact.id}
                primeStates={primeartifact.states || []}
                primeProcesses={primeartifact.processes || []}
                initialData={[selectedComponent.data]}
                onSubmit={(data: unknown) =>
                  handleComponentFormSubmit(data as Record<string, unknown>)
                }
                onBack={handleComponentFormCancel}
              />
            )}
            {selectedComponent.type === 'process' && (
              <PrimeProcessForm
                initialData={selectedComponent.data}
                onSubmit={(process: unknown) =>
                  handleComponentFormSubmit(process as Record<string, unknown>)
                }
                onCancel={handleComponentFormCancel}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GPTGenerationTab;
